import { useMutation, useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../../api';
import {
  GetAllCompanyDocumentResellsResponse,
  GetResellBuybacksResponse,
  PreviewDeleteResponse,
  RetryCessionPayload,
} from './types';

export const getAllFactoringReSelling = async (
  dispatch: Dispatch,
  query: string,
): Promise<GetAllCompanyDocumentResellsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllActiveResellsForFund = async (
  dispatch: Dispatch,
  fundId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/fund/${fundId}/resells/active`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAllFactoringReSellingNominees = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/nominees?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateFactoringNomineeAmountToReceive = async (
  dispatch: Dispatch,
  nomineeId: number,
  amountToReceive: number,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/reSell/factoring/nominees/amountToReceive`,
    data: {
      nomineeId,
      amountToReceive,
    },
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentsAvailableForReSelling = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/documents/available`,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentsAvailableForReplacement = async (
  dispatch: Dispatch,
  id: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/${id}/available-documents-for-replacement?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const replaceResellDocument = async (
  dispatch: Dispatch,
  id: string,
  data: { newDocumentId: string },
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/reSell/factoring/${id}/replace-document`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createFactoringReSell = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/factoring`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createFactoringReSellBatch = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/factoring/batch`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getFactoringReSell = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateFactoringReSell = async (
  dispatch: Dispatch,
  id: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/reSell/factoring/${id}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};
export const updateFactoringReSellBatch = async (
  dispatch: Dispatch,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/reSell/factoring/batch`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getResellFactoringMissingApprovals = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/${id}/approvals`,
    dispatch,
  });
  if (response) return response.data;
};

export const approveResell = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/factoring/${id}/approve`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const rejectResell = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/factoring/${id}/reject`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getResellPayments = async (dispatch: Dispatch, id: number) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/${id}/payments`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAECsInPosession = async (
  dispatch: Dispatch,
  documentIds: {
    id: string;
    folio: string;
    businessIdentifier: string;
    cessionDate: Date;
  }[],
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/getAecToResell`,
    data: { documentIds, useLegacy: true },
    dispatch,
  });
  if (response) return response.data;
};

export const downloadAEC = async (
  dispatch: Dispatch,
  documentIds: string[],
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/downloadAEC`,
    data: { documentIds },
    dispatch,
  });
  if (response) return response.data;
};

export const executeRecesion = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/executeReCesion`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getDocumentsPreparedForResell = async (
  dispatch: Dispatch,
  documentIds: string[],
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/reSell/factoring/prepareDocumentsForResell`,
    data: { documentIds },
    dispatch,
  });
  if (response) return response.data;
};

export const getSuggestedResellsForConciliation = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/suggestedConciliation?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCessionStatusBatch = async (
  dispatch: Dispatch,
  payload: Array<{
    folio: string;
    docType: string;
    businessIdentifier: string;
  }>,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/integrations/cessionDTE/query-status`,
    data: payload,
    dispatch,
  });
  if (response) return response.data;
};

export const retryCession = async (
  dispatch: Dispatch,
  payload: RetryCessionPayload,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/integrations/cessionDTE/retry`,
    data: payload,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteFactoringNominee = async (
  dispatch: Dispatch,
  nomineeId: string | number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/reSell/factoring/nominees/${nomineeId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteFactoringNomineePreview = async (
  dispatch: Dispatch,
  id: string | number,
): Promise<PreviewDeleteResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/factoring/nominees/${id}/delete-preview`,
    dispatch,
  });
  if (response) return response.data;
};

export const getResellBuybacks = async (
  dispatch: Dispatch,
  query: string,
): Promise<GetResellBuybacksResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/reSell/buybacks?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const checkRevertResellBuyback = (dispatch: Dispatch, id: number) => {
  return useQuery({
    queryKey: ['reSell', 'buybacks', id, 'revert', 'check'],
    queryFn: async () => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/reSell/buybacks/${id}/revert/check`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!id,
    retry: 1,
  });
};

export const revertResellBuyback = (dispatch: Dispatch, id: number) => {
  return useMutation({
    mutationKey: ['reSell', 'buybacks', id, 'revert'],
    mutationFn: async (): Promise<{ message: string } | undefined> => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/reSell/buybacks/${id}/revert`,
        dispatch,
      });
      if (response) return response.data;
    },
  });
};
